import { useEffect, useState } from 'preact/hooks';
import { api, cerrarSesion, type Emisor, fmtRut, haySesion, type Sesion } from './api';
import { Icono, Logo } from './iconos';
import { Administracion } from './pantallas/Administracion';
import { Configurar2fa } from './pantallas/Configurar2fa';
import { Inicio } from './pantallas/Inicio';
import { Ingreso } from './pantallas/Ingreso';
import { Listado } from './pantallas/Listado';
import { MiCuenta } from './pantallas/MiCuenta';
import { Nuevo } from './pantallas/Nuevo';

type Vista = { n: 'inicio' } | { n: 'lista' } | { n: 'nuevo' } | { n: 'doc'; id: string } | { n: 'admin' } | { n: 'cuenta' };

const MODULOS: Record<string, { txt: string; ic: string; nuevo?: boolean }> = {
  recurrentes: { txt: 'Facturación mensual', ic: 'calendario' }, arriendos: { txt: 'Arriendos · 11%', ic: 'casa' }, inmo: { txt: 'Inmobiliaria · venta por rol', ic: 'edificio' },
  ceec: { txt: 'Estados de pago · CEEC', ic: 'casco' }, admin: { txt: 'Propietarios y estadías', ic: 'personas', nuevo: true }, boletas: { txt: 'Boletas · emisión rápida', ic: 'boleta' },
};

export function App() {
  const [sesion, setSesion] = useState<Sesion | null>(null);
  const [emisores, setEmisores] = useState<Emisor[]>([]);
  const [emisorId, setEmisorId] = useState(localStorage.getItem('emisor') ?? '');
  const [menuEmisor, setMenuEmisor] = useState(false);
  const [lateral, setLateral] = useState(false);
  const [vista, setVista] = useState<Vista>({ n: 'inicio' });
  const [salud, setSalud] = useState<{ ok: boolean; ambiente: string } | null>(null);
  const [listo, setListo] = useState(false);

  async function cargar() {
    if (!haySesion()) { setSesion(null); setListo(true); return; }
    try {
      const s = await api<Sesion>('/api/sesion');
      const es = await api<Emisor[]>('/api/emisores');
      setSesion(s); setEmisores(es);
      if (!es.find((e) => e.id === emisorId) && es[0]) setEmisorId(es[0].id);
      api<{ ok: boolean; ambiente: string }>('/api/salud').then(setSalud).catch(() => setSalud({ ok: false, ambiente: '' }));
    } catch { setSesion(null); }
    setListo(true);
  }
  useEffect(() => { cargar(); }, []);
  useEffect(() => { if (emisorId) localStorage.setItem('emisor', emisorId); }, [emisorId]);

  if (!listo) return <div class="ingreso cargando">Cargando…</div>;
  if (!sesion) return <Ingreso alEntrar={cargar} />;
  const emisor = emisores.find((e) => e.id === emisorId);
  if (!emisor) return <div class="ingreso"><div class="tarjeta relleno">Tu usuario no tiene emisores asignados. Pide al administrador que te asocie uno.</div></div>;
  const ini = (n: string) => n.split(/\s+/).filter((p) => p.length > 2 && !/^(de|y|e|la|los|las|del)$/i.test(p)).slice(0, 3).map((p) => p[0]).join('').toUpperCase();
  const ir = (v: Vista) => { setVista(v); setLateral(false); };

  return (
    <div class="app">
      <header class="barra">
        <button class="boton chico hamburguesa" aria-label="Menú" onClick={() => setLateral(!lateral)}><Icono n="menu" /></button>
        <Logo />
        <div style="position:relative">
          <button class="selector-emisor" aria-label="Cambiar emisor" onClick={() => setMenuEmisor(!menuEmisor)}>
            <span class="ini">{ini(emisor.razon_social)}</span>
            <span class="txt"><b>{emisor.razon_social}</b><small class="num">RUT {fmtRut(emisor.rut)} · {emisor.segmento.replace('_', ' ')}</small></span>
            <Icono n="abajo" tam={18} />
          </button>
          {menuEmisor && (
            <div class="menu-flotante">
              <div class="sub" style="padding:6px 10px">Emisores autorizados para este firmante</div>
              {emisores.map((e) => <button key={e.id} class={e.id === emisorId ? 'activo' : ''} onClick={() => { setEmisorId(e.id); setMenuEmisor(false); ir({ n: 'lista' }); }}><span class="ini">{ini(e.razon_social)}</span><span style="display:flex;flex-direction:column;line-height:1.15"><b>{e.razon_social}</b><small class="sub num">{fmtRut(e.rut)} · {e.ambiente}</small></span></button>)}
            </div>
          )}
        </div>
        <div style="margin-left:auto;display:flex;align-items:center;gap:16px">
          <div class={`estado-sii ${salud?.ok ? '' : 'off'}`} title="Conexión con el SII"><b style="font-style:italic;font-size:18px;color:var(--marino)">SII</b><span style={`width:10px;height:10px;border-radius:50%;background:${salud?.ok ? '#1E9E5A' : '#B45309'}`} />{salud?.ok ? `Servicio activo · ambiente de ${salud.ambiente}` : 'Servicio no disponible'}</div>
          <button class="usuario" title="Mi cuenta" aria-label="Mi cuenta" style="background:none;border:0;padding:0;cursor:pointer;font:inherit;color:inherit;text-align:left" onClick={() => ir({ n: 'cuenta' })}><div class="txt"><div style="font-weight:600">{sesion.correo}</div><small>{({ administrador: 'Administrador', emisor: 'Emisor', lectura: 'Solo lectura' })[sesion.rol]}</small></div><div class="avatar">{ini(sesion.nombre)}</div></button>
          <button class="boton chico" aria-label="Salir" title="Salir" onClick={() => { cerrarSesion(); location.reload(); }}><Icono n="salir" tam={18} /></button>
        </div>
      </header>
      <div class="cuerpo">
        <nav class={`lateral ${lateral ? 'abierto' : ''}`} aria-label="Módulos">
          <button class={`nav-item ${vista.n === 'inicio' ? 'activo' : ''}`} onClick={() => ir({ n: 'inicio' })}><Icono n="inicio" />Inicio</button>
          <button class={`nav-item ${vista.n === 'nuevo' ? 'activo' : ''}`} onClick={() => ir({ n: 'nuevo' })}><span class="punto-nuevo"><Icono n="mas" tam={12} /></span>Nuevo documento</button>
          <button class="nav-item" onClick={() => ir({ n: 'lista' })}><Icono n="ventas" /><span class="espacio">Ventas</span><Icono n="abajo" tam={16} /></button>
          <button class={`nav-item nav-sub ${vista.n === 'lista' ? 'activo' : ''}`} onClick={() => ir({ n: 'lista' })}>Listado de documentos</button>
          <div class="nav-titulo">Módulos de {ini(emisor.razon_social)}</div>
          {emisor.modulos.map((m) => MODULOS[m] && <button key={m} class="nav-item" title="Se habilita en su etapa"><Icono n={MODULOS[m].ic} /><span class="espacio">{MODULOS[m].txt}</span>{MODULOS[m].nuevo ? <span class="etiqueta">Nuevo</span> : <Icono n="abajo" tam={16} />}</button>)}
          <div class="nav-titulo">General</div>
          <button class="nav-item" title="Etapa siguiente"><Icono n="reportes" /><span class="espacio">Reportes</span><Icono n="abajo" tam={16} /></button>
          {sesion.rol === 'administrador' && <button class={`nav-item ${vista.n === 'admin' ? 'activo' : ''}`} onClick={() => ir({ n: 'admin' })}><Icono n="ajustes" /><span class="espacio">Administración</span></button>}
          <button class={`nav-item ${vista.n === 'cuenta' ? 'activo' : ''}`} onClick={() => ir({ n: 'cuenta' })}><Icono n="personas" /><span class="espacio">Mi cuenta</span></button>
        </nav>
        <main class="principal">
          {sesion.sin2fa && <Configurar2fa alListo={cargar} />}
          {vista.n === 'inicio' && <Inicio emisor={emisor} nombre={sesion.nombre} alAbrir={(id) => ir({ n: 'doc', id })} alNuevo={() => ir({ n: 'nuevo' })} alCambiarEmisor={(id) => { setEmisorId(id); ir({ n: 'lista' }); }} />}
          {vista.n === 'admin' && <Administracion emisores={emisores} alCambio={cargar} />}
          {vista.n === 'cuenta' && <MiCuenta sesion={sesion} />}
          {vista.n === 'lista' && <Listado emisor={emisor} alNuevo={() => ir({ n: 'nuevo' })} alAbrir={(id) => ir({ n: 'doc', id })} />}
          {vista.n === 'nuevo' && <Nuevo key={emisor.id} emisor={emisor} alVolver={() => ir({ n: 'lista' })} />}
          {vista.n === 'doc' && <Nuevo key={vista.id} emisor={emisor} documentoId={vista.id} alVolver={() => ir({ n: 'lista' })} />}
        </main>
      </div>
    </div>
  );
}
